import { ModifiersZTableModel, TTableOptions } from '@monorepo/ui';
import { TCssSize, TTableCellTree, TTableFooterNode } from '../../../table.type';
import { TCentralizedTableWidgetMessage, TCentralizedTableWidgetData } from '@monorepo/types';
import { roundOffNumericText } from '@monorepo/utils';

export function fillEmptyRows<T>(data: T[], totalRows: number): T[] {
  if (data.length < totalRows) {
    const emptyRows = Array.from({ length: totalRows - data.length }, () => [] as unknown as T);
    return data.concat(emptyRows);
  }
  return data;
}
export const DEFAULT_ROW_CELL_FONT_SIZE_IN_VH = 2.5;

const DEFAULT_SUBTITLE_FONT_SIZE_IN_VH = 1.75;

const COLOR_MAP: Partial<Record<string, string>> = {
  RED: '#fa2049',
  GREEN: '#35ce9a',
  WHITE: '#fff',
  YELLOW: '#ffc91a',
};

const ICON_MAP = {
  DOWN_ARROW: 'RED_DOWN_ARROW' as const,
  UP_ARROW: 'GREEN_UP_ARROW' as const,
};

export const CENTRALIZED_TABLE_MODEL = {
  createModel: (
    widgetData: TCentralizedTableWidgetData,
    data: TCentralizedTableWidgetMessage['series'],
    totalsRow?: TCentralizedTableWidgetMessage['series'][number],
  ) => {
    data = fillEmptyRows(data, widgetData.pagination.itemsPerPage);

    return ModifiersZTableModel.createTableModel(
      data,
      {
        headerConfig: createHeaderConfig(widgetData),
        ...(totalsRow
          ? {
              footerConfig: createFooterConfig(widgetData, totalsRow),
            }
          : {}),
      },
      (item) => ({
        type: 'ROW',
        rowItem: item,
        cells: createRowCells(widgetData, item),
      }),
    );
  },

  createIdentityModel: (widgetData: TCentralizedTableWidgetData) => {
    return ModifiersZTableModel.createTableModel(
      [] as TCentralizedTableWidgetMessage['series'],
      {
        headerConfig: createHeaderConfig(widgetData),
      },
      (item) => ({
        type: 'ROW',
        rowItem: item,
        cells: [],
      }),
    );
  },
};

function createHeaderConfig(widgetData: TCentralizedTableWidgetData): TTableOptions['headerConfig'] {
  return widgetData.tableColumns
    .filter((col) => !col.hidden)
    .map((col): TTableOptions['headerConfig'][number] => ({
      type: 'TEXT_CELL' as const,
      text: col.name || '',
      fontSize: { value: DEFAULT_ROW_CELL_FONT_SIZE_IN_VH * widgetData.headerFontSizeMultiplier, unit: 'vh' },
      maxWidthInPx: 100,
      ...(col.subtitle
        ? {
            subtitle: {
              text: col.subtitle,
              fontSize: { value: DEFAULT_SUBTITLE_FONT_SIZE_IN_VH * widgetData.headerFontSizeMultiplier, unit: 'vh' },
              textColor: 'WHITE',
            },
          }
        : {}),
      textOverflow: 'nowrap',
      align: col.fontAlign ?? 'left',
    }));
}

function createFooterConfig(
  widgetData: TCentralizedTableWidgetData,
  data: TCentralizedTableWidgetMessage['series'][number],
): TTableOptions['footerConfig'] {
  return widgetData.tableColumns.map((col): TTableFooterNode['cells'][number] => {
    const cellData = data.find((i) => i.attribute === col.prop)?.data;
    return {
      type: 'TEXT_LIST_CELL' as const,
      ...getCellTextAndColor(cellData, col.roundOff),
      fontSize: getFooterFontSize(widgetData.footerFontSizeMultiplier, cellData),
      maxWidthInPx: 100,
      textAlign: col.fontAlign ?? 'left',
      orientation: cellData?.text_orientation ?? 'HORIZONTAL',
      textOverflow: 'nowrap',
      gap: { value: cellData?.text_orientation === 'VERTICAL' ? 0.0 : 0.5, unit: 'vw' },
      ...(cellData?.icon && cellData.icon !== 'NONE'
        ? {
            marker: {
              color: '',
              iconId: ICON_MAP[cellData.icon],
              customSize: {
                height: { value: 3, unit: 'vh' },
                width: { value: 3, unit: 'vh' },
              },
            },
          }
        : {}),
    };
  });
}

function createRowCells(
  widgetData: TCentralizedTableWidgetData,
  item: TCentralizedTableWidgetMessage['series'][number],
): TTableCellTree[] {
  return widgetData.tableColumns
    .filter((col) => !col.hidden)
    .map((col): TTableCellTree => {
      const cellData = item.find((i) => i.attribute === col.prop)?.data;
      const { text, textColorHexCodes } = getCellTextAndColor(cellData, col.roundOff);
      const cssAnimationDuration = getAnimationDuration(text, col.scrollLeftAnimationSpeed);

      return {
        type: 'TEXT_LIST_CELL' as const,
        text,
        textColorHexCodes,
        textAlign: col.fontAlign ?? 'left',
        orientation: 'HORIZONTAL',
        textOverflow: 'nowrap',
        animate: col.scrollLeftAnimation && !!cellData,
        cssAnimationDuration,
        fontSize: { value: DEFAULT_ROW_CELL_FONT_SIZE_IN_VH * widgetData.rowsFontSizeMultiplier, unit: 'vh' },
        gap: { value: 0.5, unit: 'vw' },
        ...(cellData?.icon && cellData.icon !== 'NONE'
          ? {
              marker: {
                color: '',
                iconId: ICON_MAP[cellData.icon],
                customSize: {
                  height: { value: 3, unit: 'vh' },
                  width: { value: 3, unit: 'vh' },
                },
              },
            }
          : {}),
      };
    });
}

function getCellTextAndColor(
  item?: TCentralizedTableWidgetMessage['series'][number][number]['data'],
  roundOff?: number,
): {
  text: string[];
  textColorHexCodes: string[];
} {
  if (!item) return { text: ['- -'], textColorHexCodes: ['#fff'] };

  return {
    text: item.text.map((i) => roundOffNumericText(i.value, roundOff)),
    textColorHexCodes: item.text.map((i) => COLOR_MAP[i.color] ?? i.color),
  };
}

function getFooterFontSize(
  fontSizeMultiplier: number,
  item?: TCentralizedTableWidgetMessage['series'][number][number]['data'],
): TCssSize {
  if (item && item.text.length > 1) {
    return { value: 1.8 * fontSizeMultiplier, unit: 'vh' };
  } else {
    return { value: 2.5 * fontSizeMultiplier, unit: 'vh' };
  }
}

function getAnimationDuration(text: string[], speed?: number): string {
  const textLen = text.reduce((total, curr) => curr.length + total, 0);

  return textLen === 0 ? '0s' : `${(textLen * (speed ?? 1)) / 2}s`;
}
